import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Edit } from 'lucide-react';
import AuthContext from '../../../../../context/AuthContext';
import { useToast } from '../../../../../context/ToastContext';
import historyService from '../../../../../services/historyService';

export default function ProcessLogsDetailes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);
  const { showError } = useToast();
  
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProcessLogDetail();
  }, [id]);

  const fetchProcessLogDetail = async () => {
    setLoading(true);
    try {
      const result = await historyService.getProcessHistoryDetail(id, authTokens.access);
      if (result.success) {
        setLog(result.data);
      } else {
        showError(`Erreur: ${result.error}`);
        setTimeout(() => navigate(-1), 2000);
      }
    } catch (error) {
      showError("Impossible de charger le détail de l'historique");
      setTimeout(() => navigate(-1), 2000);
    } finally {
      setLoading(false);
    }
  };

  const renderChanges = (changes) => {
    if (!changes) return <p className="text-sm text-gray-500">Aucun changement détaillé enregistré.</p>;
    
    const renderActivityChanges = (oldActivities, newActivities) => {
      return (
        <div className="ml-4 space-y-2">
          <div>
            <span className="text-sm font-medium text-red-600">Anciennes activités:</span>
            <div className="ml-2 text-xs text-gray-600">
              {oldActivities.length === 0 ? (
                <span className="italic">Aucune activité</span>
              ) : (
                oldActivities.map((activity, index) => (
                  <div key={index} className="border-l-2 border-red-200 pl-2 mb-1">
                    <div><strong>Nom:</strong> {activity.activity_name}</div>
                    <div><strong>Description:</strong> {activity.description}</div>
                    <div><strong>Statut:</strong> {activity.status}</div>
                  </div>
                ))
              )}
            </div>
          </div>
          <div>
            <span className="text-sm font-medium text-green-600">Nouvelles activités:</span>
            <div className="ml-2 text-xs text-gray-600">
              {newActivities.length === 0 ? (
                <span className="italic">Aucune activité</span>
              ) : (
                newActivities.map((activity, index) => (
                  <div key={index} className="border-l-2 border-green-200 pl-2 mb-1">
                    <div><strong>Nom:</strong> {activity.activity_name}</div>
                    <div><strong>Description:</strong> {activity.description}</div>
                    <div><strong>Statut:</strong> {activity.status}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      );
    };

    const renderReductionActionChanges = (oldActions, newActions) => {
      return (
        <div className="ml-4 space-y-2">
          <div>
            <span className="text-sm font-medium text-red-600">Anciennes actions de réduction:</span>
            <div className="ml-2 text-xs text-gray-600">
              {oldActions.length === 0 ? (
                <span className="italic">Aucune action</span>
              ) : (
                oldActions.map((action, index) => (
                  <div key={index} className="border-l-2 border-red-200 pl-2 mb-1">
                    <div><strong>Action:</strong> {action.action}</div>
                    <div><strong>Date d'action:</strong> {action.actionDate || 'Non définie'}</div>
                  </div>
                ))
              )}
            </div>
          </div>
          <div>
            <span className="text-sm font-medium text-green-600">Nouvelles actions de réduction:</span>
            <div className="ml-2 text-xs text-gray-600">
              {newActions.length === 0 ? (
                <span className="italic">Aucune action</span>
              ) : (
                newActions.map((action, index) => (
                  <div key={index} className="border-l-2 border-green-200 pl-2 mb-1">
                    <div><strong>Action:</strong> {action.action}</div>
                    <div><strong>Date d'action:</strong> {action.actionDate || 'Non définie'}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <ul className="space-y-3">
        {Object.entries(changes).map(([field, values]) => (
          <li key={field} className="text-sm">
            <strong className="font-semibold text-gray-800">{field}:</strong>
            {field === 'activities' ? (
              renderActivityChanges(values.from, values.to)
            ) : field === 'reduction_actions' ? (
              renderReductionActionChanges(values.from, values.to)
            ) : (
              <span>
                {" "}
                <span className="text-red-600">'{values.from}'</span> {"->"} <span className="text-green-600">'{values.to}'</span>
              </span>
            )}
          </li>
        ))}
      </ul>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!log) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Détail non trouvé.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Retour
        </button>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Détail de l'Historique du Processus</h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              log.action_type === 'create' ? 'bg-green-100 text-green-800' :
              log.action_type === 'update' ? 'bg-blue-100 text-blue-800' :
              log.action_type === 'delete' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {log.action_type === 'create' ? 'Création' :
               log.action_type === 'update' ? 'Modification' :
               log.action_type === 'delete' ? 'Suppression' :
               log.action_type}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
            <div className="flex items-center">
              <User className="h-4 w-4 mr-2 text-gray-400" />
              <span><strong>Fait par:</strong> {log.done_by_email}</span>
            </div>
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-gray-400" />
              <span><strong>Date:</strong> {new Date(log.action_date).toLocaleDateString('fr-FR')}</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2 text-gray-400" />
              <span><strong>Heure:</strong> {new Date(log.action_date).toLocaleTimeString('fr-FR')}</span>
            </div>
            <div className="flex items-center">
              <Edit className="h-4 w-4 mr-2 text-gray-400" />
              <span><strong>Nom du processus:</strong> {log.process_name}</span>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Description</h3>
            <p className="text-gray-700">{log.description}</p>
          </div>

          {log.changes_summary && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Changements Détaillés</h3>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                {renderChanges(log.changes_summary)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
